/*
 * <bundle-picker> (snippets/bundle-picker.liquid): "bundle & save" rows on the product page.
 * 1 = the normal add to cart above. 2 or 3 opens one row per piece (this piece first), each with its own piece,
 * colour and size, and adds them all at once. The price shown is a preview; checkout's bundle discount charges it.
 */
class BundlePicker extends HTMLElement {
  connectedCallback() {
    this.tiers = JSON.parse(this.querySelector('[data-bundle-tiers]').textContent).map((t) => ({ quantity: Number(t.quantity), price: Number(t.cents) }));
    this.pieces = JSON.parse(this.querySelector('[data-bundle-pieces]').textContent).filter((p) => p.variants.length);
    this.build = this.querySelector('[data-bundle-build]');
    this.slotList = this.querySelector('[data-bundle-slots]');
    this.submit = this.querySelector('[data-bundle-submit]');
    this.error = this.querySelector('[data-bundle-error]');
    this.slots = [];

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) return this.choose(Number(event.target.value));
      const row = event.target.closest('[data-slot]');
      if (!row) return;
      const slot = this.slots[Number(row.dataset.slot)];
      if (event.target.matches('[data-slot-piece]')) {
        slot.piece = this.pieces.find((p) => p.id === Number(event.target.value));
        slot.variant = slot.piece.variants[0];
      } else {
        const index = Number(event.target.dataset.slotOption);
        slot.variant = this.match(slot.piece, slot.variant, index, event.target.value);
      }
      this.renderSlots();
    });

    this.submit.addEventListener('click', () => this.addToCart());

    // Picks from earlier in this visit (e.g. after looking at a piece's page and coming back)
    this.storageKey = `sunday:bundle-picker:${location.pathname}`;
    const saved = this.restore();
    if (saved) {
      const radio = this.querySelector(`input[type="radio"][value="${saved.quantity}"]`);
      if (radio) radio.checked = true;
    }

    // Home page: the smallest bundle starts picked, so the rows are open straight away
    const checked = this.querySelector('input[type="radio"]:checked');
    if (checked) this.choose(Number(checked.value));
  }

  money(cents) {
    const amount = (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 });
    return `<span class="money">${amount} ${this.dataset.currency}</span>`;
  }

  restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
      if (!saved?.slots) return null;
      this.slots = saved.slots.map((s) => {
        const piece = this.pieces.find((p) => p.id === s.piece);
        const variant = piece?.variants.find((v) => v.id === s.variant);
        return piece && variant ? { piece, variant } : null;
      }).filter(Boolean);
      return saved;
    } catch {
      return null;
    }
  }

  save(clear) {
    try {
      if (clear) return sessionStorage.removeItem(this.storageKey);
      sessionStorage.setItem(this.storageKey, JSON.stringify({ quantity: this.tier.quantity, slots: this.slots.map((s) => ({ piece: s.piece.id, variant: s.variant.id })) }));
    } catch {
      // Storage blocked (private browsing): picks just aren't remembered
    }
  }

  // The piece dropdown, grouped by collection (hoodies, zip-ups, fleeces) in the bundle's order
  pieceOptions(selectedId) {
    const groups = [...new Set(this.pieces.map((p) => p.group))];
    const option = (p) => `<option value="${p.id}"${p.id === selectedId ? ' selected' : ''}>${p.title}</option>`;
    if (groups.length < 2) return this.pieces.map(option).join('');
    return groups.map((group) => `<optgroup label="${group}">${this.pieces.filter((p) => p.group === group).map(option).join('')}</optgroup>`).join('');
  }

  // The in-stock variant with the new option value that keeps as many of the other choices as possible
  match(piece, current, index, value) {
    const options = [...current.options];
    options[index] = value;
    const score = (v) => v.options.reduce((n, o, i) => n + (o === options[i] ? 1 : 0), 0);
    return piece.variants.filter((v) => v.options[index] === value).sort((a, b) => score(b) - score(a))[0] || current;
  }

  choose(quantity) {
    const tier = this.tiers.find((t) => t.quantity === quantity);
    this.build.hidden = !tier;
    this.querySelectorAll('.bundle-tier').forEach((label) => label.classList.toggle('is-selected', label.querySelector('input').checked));
    if (!tier) return this.save(true);

    // Product page: slot 1 is this piece in the colour/size chosen above. The others start on different pieces.
    // Home page (no data-lead): every slot picks its own piece.
    const kept = this.slots;
    this.slots = [];
    for (let i = 0; i < quantity; i += 1) {
      const piece = kept[i]?.piece || this.pieces[i % this.pieces.length];
      this.slots.push({ piece, variant: kept[i]?.variant || piece.variants[0] });
    }
    if (this.hasAttribute('data-lead')) {
      const chosenId = Number(document.querySelector('[data-product-form] [data-variant-id]')?.value);
      this.slots[0].lead = true;
      if (!kept[0]) this.slots[0].variant = this.pieces[0].variants.find((v) => v.id === chosenId) || this.pieces[0].variants[0];
    }
    this.tier = tier;
    this.renderSlots();
  }

  renderSlots() {
    this.slotList.innerHTML = this.slots.map((slot, i) => {
      const pieceField = slot.lead
        ? `<p class="bundle-slot__title">${slot.piece.title} <span>(this one)</span></p>`
        : `<select class="bundle-slot__select bundle-slot__select--piece" data-slot-piece aria-label="piece ${i + 1}">
            ${this.pieceOptions(slot.piece.id)}
          </select>`;
      const optionFields = slot.piece.options.map((name, index) => {
        const values = [...new Set(slot.piece.variants.map((v) => v.options[index]))];
        if (values.length < 2 && slot.piece.options.length > 1) return '';
        // Sizes stay in capitals (XS, S, M) like the size boxes above; colours are lowercased by the CSS
        const size = /size/i.test(name) ? ' bundle-slot__select--size' : '';
        return `<select class="bundle-slot__select${size}" data-slot-option="${index}" aria-label="${name} for piece ${i + 1}">
          ${values.map((value) => `<option value="${value}"${value === slot.variant.options[index] ? ' selected' : ''}>${value}</option>`).join('')}
        </select>`;
      }).join('');
      // "view product" opens the piece in the chosen colour/size (not on the product page's own piece)
      const url = `${slot.piece.url}?variant=${slot.variant.id}`;
      const image = `<img class="bundle-slot__image" src="${slot.variant.image}" alt="" width="56" height="70" loading="lazy">`;
      return `<li class="bundle-slot" data-slot="${i}">
        <span class="bundle-slot__number">${i + 1}</span>
        <div class="bundle-slot__media">
          ${slot.lead ? image : `<a href="${url}" tabindex="-1" aria-hidden="true">${image}</a><a href="${url}" class="bundle-slot__link">view product</a>`}
        </div>
        <div class="bundle-slot__fields">${pieceField}<div class="bundle-slot__options">${optionFields}</div></div>
      </li>`;
    }).join('');

    const full = this.slots.reduce((sum, s) => sum + s.variant.price, 0);
    const total = Math.min(full, this.tier.price);
    const word = this.tier.quantity === 1 ? this.dataset.word : this.dataset.words;
    this.submit.innerHTML = `add ${this.tier.quantity} ${word} · ${total < full ? `<s>${this.money(full)}</s> ` : ''}${this.money(total)}`;

    // The picked row shows the same saving as the button (the other rows keep their estimate)
    const save = this.querySelector(`[data-tier-save="${this.tier.quantity}"]`);
    const price = this.querySelector(`[data-tier-price="${this.tier.quantity}"]`);
    if (save) save.innerHTML = total < full ? `save ${this.money(full - total)}` : '';
    if (price) price.innerHTML = `${total < full ? `<s>${this.money(full)}</s> ` : ''}${this.money(total)}`;
    this.submit.disabled = false;
    this.error.hidden = true;
    this.save();
  }

  async addToCart() {
    this.submit.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: this.slots.map((s) => ({ id: s.variant.id, quantity: 1 })), sections: ['cart-drawer'] }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'could not add your bundle');
      this.save(true);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { html: result.sections?.['cart-drawer'] } }));
      this.submit.disabled = false;
    } catch (error) {
      this.error.textContent = error.message.toLowerCase();
      this.error.hidden = false;
      this.submit.disabled = false;
    }
  }
}

if (!customElements.get('bundle-picker')) customElements.define('bundle-picker', BundlePicker);
